import { createEnum } from '../helpers'

export const BPC_ACTUALS_IMPORT_ERRORS = ['missingMaterial', 'missingSubChannel'] as const
export const BPC_ACTUALS_IMPORT_ERROR_TYPES = createEnum(BPC_ACTUALS_IMPORT_ERRORS)
export type BpcActualsImportErrorType = (typeof BPC_ACTUALS_IMPORT_ERRORS)[number]

/**
 * Number of monthly columns provided per KPI in the BPC actuals CSV
 * (e.g. `MPVO10000_Volume_01` ... `MPVO10000_Volume_12`).
 */
export const BPC_ACTUALS_MONTH_COUNT = 12

/**
 * KPI definitions for the BPC actuals CSV import.
 *
 * Each KPI contributes 12 columns to the CSV, named `${code}_${name}_${MM}`
 * where `MM` is the 1-based, zero-padded month (`01`..`12`), e.g.
 * `MPVO10000_Volume_01`, ..., `MPCO00000_TotalCostOfSales_12`.
 *
 * `name` is the PascalCase token used in the column header. Only `Volume` and
 * `TotalCostOfSales` were provided; the rest are derived from the original
 * column name (in comments) and should be confirmed against the real CSV
 * headers before parsing.
 */
export const BPC_ACTUALS_KPIS = [
  { code: 'MPVO10000', name: 'Volume' }, // Sales Volume (in base UoM)
  { code: 'MPTO13000', name: 'CPTO' }, // Consumer price turnover (CPTO)
  { code: 'MPGT12000', name: 'ExternalDirectExciseSpecific' }, // External direct excise specific
  { code: 'MPGT14000', name: 'ExternalDirectOtherGovernmentLevies' }, // External direct other government levies
  { code: 'MPTO44000', name: 'IndirectOtherGovernmentLevies' }, // Indirect other government levies
  { code: 'MPGT11000', name: 'ExternalDirectVAT' }, // External direct VAT
  { code: 'MPTO41000', name: 'IndirectVAT' }, // Indirect VAT
  { code: 'MPTO22022', name: 'CustomerAllowancesSurcharges' }, // Customer allowances (surcharges)
  { code: 'MPTO22021', name: 'CustomerAllowancesDiscounts' }, // Customer allowances (discounts)
  { code: 'MPTO22011', name: 'CustomerPriceDiscounts' }, // Customer price discounts
  { code: 'MPTO60010', name: 'OtherAdjustmentsExternalGTO' }, // Other adjustments in external GTO expense/(income) => Unsellables
  { code: 'MPTO22040', name: 'EarlyPaymentAllowances' }, // Early payment allowances
  { code: 'MPTO22030', name: 'TradePayForPerformance' }, // Trade pay for performance
  { code: 'MPTO11000', name: 'RRP' }, // Recommended retail price (RRP)
  { code: 'MPTO14000', name: 'CPTOAdjustment' }, // CPTO adjustment income/(expense)
  { code: 'MPTO21010P', name: 'RetailerMargin' }, // Retailer margin
  { code: 'MPTO21020P', name: 'WholesaleMargin' }, // Wholesale margin
  { code: 'MPTO30000', name: 'MarketingInvestmentGTO' }, // Marketing investment in GTO
  { code: 'MPNT00000', name: 'TotalNetTurnover' }, // Total net turnover
  { code: 'MPCO30000', name: 'Royalties' }, // Royalties
  { code: 'MPCO22000', name: 'InternallyBoughtInCostOfSales' }, // Internally bought-in cost of sales
  { code: 'MPCO10000', name: 'ExternallyBoughtInCostOfSales' }, // Externally bought-in cost of sales
  { code: 'MPCO00000', name: 'TotalCostOfSales' }, // Total cost of sales
] as const

type BpcActualsKpi = (typeof BPC_ACTUALS_KPIS)[number]
export type BpcActualsKpiCode = BpcActualsKpi['code']
export type BpcActualsKpiName = BpcActualsKpi['name']

const BPC_ACTUALS_KPI_NAME_TO_CODE = Object.fromEntries(
  BPC_ACTUALS_KPIS.map((kpi) => [kpi.name, kpi.code]),
) as Record<BpcActualsKpiName, BpcActualsKpiCode>

const BPC_ACTUALS_KPI_CODE_TO_NAME = Object.fromEntries(
  BPC_ACTUALS_KPIS.map((kpi) => [kpi.code, kpi.name]),
) as Record<BpcActualsKpiCode, BpcActualsKpiName>

export const getBpcKpiCode = (name: BpcActualsKpiName): BpcActualsKpiCode =>
  BPC_ACTUALS_KPI_NAME_TO_CODE[name]

export const getBpcKpiName = (code: BpcActualsKpiCode): BpcActualsKpiName =>
  BPC_ACTUALS_KPI_CODE_TO_NAME[code]

/** All KPI codes as a tuple, usable for `z.enum(...)`. */
export const BPC_ACTUALS_KPI_CODES = BPC_ACTUALS_KPIS.map((kpi) => kpi.code) as [
  BpcActualsKpiCode,
  ...BpcActualsKpiCode[],
]

/** Builds the `${code}_${name}_${MM}` column key for a KPI and 1-based month. */
export const bpcActualsKpiColumnKey = (code: string, name: string, month: number) =>
  `${code}_${name}_${String(month).padStart(2, '0')}`

/** All KPI column keys in CSV order (`MPVO10000_Volume_01` ... `MPCO00000_TotalCostOfSales_12`). */
export const BPC_ACTUALS_KPI_COLUMNS = BPC_ACTUALS_KPIS.flatMap((kpi) =>
  Array.from({ length: BPC_ACTUALS_MONTH_COUNT }, (_, index) =>
    bpcActualsKpiColumnKey(kpi.code, kpi.name, index + 1),
  ),
)

export const BPC_ACTUALS_CREATE_ERRORS = ['yearConflict'] as const

export const BPC_ACTUALS_CREATE_ERROR_TYPES = createEnum(BPC_ACTUALS_CREATE_ERRORS)
export type BpcActualsCreateErrorType = (typeof BPC_ACTUALS_CREATE_ERRORS)[number]

export const BPC_ACTUALS_CREATE_CONSTRAINT_TO_ERROR: Record<string, BpcActualsCreateErrorType> = {
  bpc_actuals_year_unique: BPC_ACTUALS_CREATE_ERROR_TYPES.yearConflict,
}
